import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectProps,
  SxProps,
  TextField,
  Theme,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { Search } from '@mui/icons-material'

interface SelectOption {
  id: number
  description: string
}

type SelectMuiProps = SelectProps & {
  label: string
  options: SelectOption[]
  helperText?: string
  optionNone?: boolean
  fullWidth?: boolean
  dataTestId?: string
  searchBar?: boolean
  searchBarDataTestId?: string
  renderAfterInputSearchBar?: boolean
  maxHeight?: string
  formControlSx?: SxProps<Theme> | undefined
}

export const SelectMui = ({
  size = 'small',
  label,
  options,
  helperText,
  optionNone = true,
  fullWidth = true,
  dataTestId,
  searchBar = false,
  searchBarDataTestId,
  renderAfterInputSearchBar = false,
  maxHeight,
  formControlSx,
  ...props
}: SelectMuiProps) => {
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')

  const displayedOptions = useMemo(() => options
    .filter((option) => option.description.toLowerCase().indexOf(searchText.toLowerCase()) > -1),
  [options, searchText])

  const labelId = `${label}-select-label`
  const resolveOptions = () => {
    if (renderAfterInputSearchBar) {
      return searchText.length > 0
        ? displayedOptions.map((option) => (
          <MenuItem key={option.id} value={option.id} data-testid={`option-${option.id}-${dataTestId}`}>
            {option.description}
          </MenuItem>
        )) : []
    }
    return displayedOptions.map((option) => (
      <MenuItem key={option.id} value={option.id} data-testid={`option-${option.id}-${dataTestId}`}>
        {option.description}
      </MenuItem>
    ))
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSearchText('')
  }

  return (
    <FormControl
      sx={formControlSx}
      size={size}
      fullWidth={fullWidth}
      data-testid={`form-${dataTestId}`}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        labelId={labelId}
        label={label}
        id={label}
        inputProps={{ 'data-testid': `input-${dataTestId}` }}
        renderValue={(selected: any) => options
          .filter((option) => option.id === selected)
          .map((option) => option.description)}
        MenuProps={{
          autoFocus: !searchBar,
          PaperProps: {
            sx: {
              maxHeight,
              '& .MuiList-root': {
                padding: 0,
              },
            },
          },
        }}
        {...props}
      >
        {searchBar
        && (
          <ListSubheader sx={{ padding: '.6rem' }}>
            <TextField
              autoFocus
              autoComplete='off'
              size='small'
              id={`${label}-select-search-bar`}
              label='Pesquisar'
              variant='standard'
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
                inputProps: {
                  'data-testid': searchBarDataTestId,
                },
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation()
                }
              }}
            />
          </ListSubheader>
        )}
        {renderAfterInputSearchBar && searchText.length === 0 && <MenuItem disabled>Pesquise para exibir as opções</MenuItem>}
        {optionNone && <MenuItem value='' data-testid={`option-none-${dataTestId}`}>Nenhuma das opções</MenuItem>}
        {resolveOptions()}
      </Select>
      {helperText && <FormHelperText error={props.error}>{helperText}</FormHelperText>}
    </FormControl>
  )
}
